import JSZip from "jszip";
import KnxBridge from "../KnxBridge";
import KnxLanguageMappingXml from "../KnxProductDataTools/LanguageMappingXml";
import Logger from "../Logger";
import { cleanEncode } from "../utils/CommonUtils";
import { loadOneProductDatamodelString } from "./KnxprodLoader";
import { json } from "stream/consumers";
import { firstChildWithTag } from "../components/XmlUtils";
import { convertXmlStringToJson } from "../extensions/imports/ets/xmlJsonUtils";

const logger = new Logger();

export type KnxProductRetailerData = {
  price: string;
  availability: string;
  ean?: string;
  page?: string;
  loadedAt: string;
};

export type IntermediateProductInfo = {
  Hardware: { [key: string]: any; ManufacturerId: string };
  ApplicationPrograms: Array<{ Id?: string; Hardware2ProgramRefId?: string; MediumType?: string }>;
  Id?: string;
  ManufacturerId?: string;
  productNames?: { [lang: string]: string };
  productDescriptions?: { [lang: string]: string };
  CatalogData?: any;
  ManufacturerData?: any;
  CatalogId?: string;
  mediumType?: string;
  IsRailMounted?: string;
  OrderNumber?: string;
  WidthInMillimeter?: string;
  SerialNumber?: string;
  BusCurrent?: string;
  IconFile?: string;
  Type?: string;
  ProductId?: string;
  // ...weitere Felder nach Bedarf ergänzen
};

export default class KnxProductInfo {
  //
  // from Hardware.xml
  //
  ProductId = "";
  Hardware2ProgramId = "";

  //
  // from Catalog.xml & main key for Estada Product Server
  //
  CatalogId = "";

  ///////////////////////////////////////////////

  ManufacturerName = "";
  ManufacturerId = "";

  SerialNumber = "";
  VersionNumber = "";
  BusCurrent = "";
  IsRailMounted = "";
  OrderNumber = "";
  WidthInMillimeter = "";
  IconFile = "";
  MediumType = "";
  DefaultLanguage = "";
  ProductNames = "";
  DefaultProductName = "";
  ProductDescriptions = "";

  Applications = "";

  ApplicationVersion = "";
  DefaultApplication = "";
  Id = "";
  Type = "";
  HasApplicationProgram = "";

  //newly added from AP
  PeiType = "";
  MaskVersion = "";
  LoadProcedureStyle = "";
  ProgramType = "";
  Linkable = "";
  ApplicationNumber = "";
  DynamicTableManagement = "";
  MinEtsVersion = "";
  CreatedFromLegacySchemaVersion = "";
  NonRegRelevantDataVersion = "";
  ReplacesVersions = "";
  Hash = "";
  OriginalManufacturer = "";

  // managed by app
  Release: string | undefined;
  Priority: "0" | "1" | "Unset" | undefined;
  Programmable: string | undefined;
  ProductType: string | undefined;
  NumberOfChannels?: number;
  ChannelType: string | undefined;
  SupportScenes: string | undefined;
  ChannelDetectionMode: string | undefined;
  HasConfig: "true" | "false" | undefined; // no itx
  NoChannels: "true" | "false" | undefined; // no itx
  DeviceAddress: string | undefined;
  SpecialUsage: string | undefined;
  IsDali: "true" | "false" | undefined; // no itx
  FromEts: string | undefined;
  ManuallyCreated: "true" | "false" | undefined; // no itx
  Variants: string | undefined;
  /** Shared shop snapshots per article number, including different product variants. */
  RetailerData?: Record<string, KnxProductRetailerData>;
  Note = "";

  UserImport?: "true" | "false" | undefined;

  //
  // Optional params only used in the UI
  // Caluclated after downloading the full list from server
  //
  ProductName: string | undefined;
  Application: string | undefined;
  ProductDescription: string | undefined;

  //
  // just during upload procedure needed
  // should not be stored in DB and deleted befor uploading
  //

  ApplicationPrograms? = new Array<string>();
}

export function getEncodedMetaData(productInfo: any): any {
  let metadata = JSON.parse(JSON.stringify(productInfo));

  // metadata.ManufacturerName = cleanEncode(
  //   metadata?.ManufacturerName || "",
  // );
  // metadata.DefaultProductName = cleanEncode(
  //   metadata?.DefaultProductName || "",
  // );
  // metadata.DefaultApplication = cleanEncode(
  //   metadata?.DefaultApplication || "",
  // );
  // metadata.Type = cleanEncode(metadata?.Type || "");

  // metadata.ProductNames = cleanEncode(
  //   JSON.stringify(metadata?.ProductNames || ""),
  // );
  // metadata.ProductDescriptions = cleanEncode(
  //   JSON.stringify(metadata?.ProductDescriptions || ""),
  // );
  // metadata.Applications = cleanEncode(
  //   JSON.stringify(metadata?.Applications || ""),
  // );

  delete metadata.ProductName;
  delete metadata.ProductDescription;
  delete metadata.Application;
  delete metadata.ApplicationPrograms;

  delete metadata.ImageExists;

  return metadata;
}

export function getEncodedOriginalMetadata(productInfo: KnxProductInfo): KnxProductInfo {
  let metadata = JSON.parse(JSON.stringify(productInfo));

  metadata.ManufacturerName = cleanEncode(metadata?.ManufacturerName || "");
  metadata.Type = cleanEncode(metadata?.Type || "");

  metadata.DefaultProductName = cleanEncode(metadata?.DefaultProductName || "");
  metadata.DefaultApplication = cleanEncode(metadata?.DefaultApplication || "");

  metadata.ProductNames = cleanEncode(JSON.stringify(metadata?.ProductNames || ""));
  metadata.ProductDescriptions = cleanEncode(JSON.stringify(metadata?.ProductDescriptions || ""));
  metadata.Applications = cleanEncode(JSON.stringify(metadata?.Applications || ""));

  if (metadata?.Variants && typeof metadata?.Variants != "string") {
    metadata.Variants = cleanEncode(JSON.stringify(metadata?.Variants || ""));
  }

  if (metadata?.ProductName == "" || metadata?.ProductName) {
    delete metadata.ProductName;
  }
  if (metadata?.ProductDescription == "" || metadata?.ProductDescription) {
    delete metadata.ProductDescription;
  }
  if (metadata?.Application == "" || metadata?.Application) {
    delete metadata.Application;
  }
  if (metadata?.originalIndex) {
    delete metadata.originalIndex;
  }
  delete metadata.ImageExists;
  return metadata;
}

export function findInHardwareContentByProductRefId(
  hardwareFileContent: any,
  productRefId: string,
  hardware2ProgramRefId: string,
): {
  Product: any | undefined;
  Hardware2Program: any | undefined;
  Hardware: any | undefined;
  ApplicationPrograms: string[] | undefined;
  Type: string | undefined;
  ManufacturerId: string | undefined;
} {
  let Product: any | undefined;
  let Hardware2Program: any | undefined;
  let Hardware: any | undefined;
  let ApplicationPrograms: string[] | undefined;
  let ManufacturerData = hardwareFileContent.KNX.ManufacturerData.Manufacturer;
  let Type;

  for (const iHardware of ManufacturerData.Hardware.items) {
    for (const iProduct of iHardware.Products.items) {
      if (iProduct.a.Id === productRefId) {
        Type = iProduct.a.Type || "";
        Product = iProduct;
        Hardware = iHardware;
        break;
      }
    }

    if (iHardware.Hardware2Programs) {
      for (const iHardware2Program of iHardware.Hardware2Programs.items) {
        if (iHardware2Program.a.Id === hardware2ProgramRefId) {
          Hardware2Program = iHardware2Program;
          ApplicationPrograms =
            iHardware2Program?.items
              ?.filter((ap: any) => ap["#name"] == "ApplicationProgramRef")
              .map((ap: any) => ap.a.RefId) || [];
          break;
        }
      }
    }
  }

  return { Product, Hardware2Program, Hardware, ApplicationPrograms, Type, ManufacturerId: ManufacturerData.a.RefId };
}

//
// <CatalogItem .../> is the entry from the Catalog.xml
// see KnxDataStructur.md
//
export async function buildExtendedProductInfo(
  mainZipFile: JSZip,
  masterInfos: {
    manufacturerInfos: any[];
    mediumTypesInfos: any[];
    maskVersionsInfo: any[];
  },
  catalogLanguageMapping: KnxLanguageMappingXml,
  CatalogItem: any,
  hardwareFileContent: any,
): Promise<KnxProductInfo> {
  let res = new KnxProductInfo();

  const catalogId: string = CatalogItem.a.Id;
  const productRefId: string = CatalogItem.a.ProductRefId;
  const hardware2ProgramRefId: string = CatalogItem.a.Hardware2ProgramRefId;

  // Force Catalog Item Default Language as Deutsch.
  // const catalogDefaultLanguage = catalogInfo.a?.DefaultLanguage;
  // if (parsedIdList.has(ProductRefId) && catalogDefaultLanguage != "de-DE") {
  //   continue;
  // }
  // parsedIdList.add(ProductRefId);

  const languageData: any = catalogLanguageMapping.getTranslation(catalogId);

  //
  // create productNames and productDescriptions as
  // productNames["de-DE"] = "irgendwas"
  // productNames["en-US"]= "something
  //
  let productNames: Record<string, string> = {};
  let productDescriptions: Record<string, string> = {};

  if (languageData) {
    Array.from(languageData).forEach((item: any) => {
      productNames[item[0]] = item[1].Name;
      productDescriptions[item[0]] = item[1].VisibleDescription;
    });
  }

  //////////////////////////////////////////////////////////////////////////////////////////////
  //
  // Pick the Entry from Hardware.xml
  // (ProductRefId, Hardware2ProgramRefId) points to the Product inside the Hardware
  //

  const { Product, Hardware2Program, Hardware, ApplicationPrograms, Type, ManufacturerId } =
    findInHardwareContentByProductRefId(hardwareFileContent, productRefId, hardware2ProgramRefId);

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  const defaultProductName = CatalogItem.a.Name?.trim() || Product.a.Text?.trim() || Hardware.a.Name?.trim() || "";
  const defaultProductDescription =
    CatalogItem.a.VisibleDescription?.trim() || Product.a.VisibleDescription?.trim() || "";
  const defaultLanguage = Product.a.DefaultLanguage?.trim() || "";

  if (defaultLanguage) {
    if (defaultProductName && !productNames[defaultLanguage]) {
      productNames[defaultLanguage] = defaultProductName;
    }
    if (defaultProductDescription && !productDescriptions[defaultLanguage]) {
      productDescriptions[defaultLanguage] = defaultProductDescription;
    }
  }

  //
  // set the main key
  //
  res.CatalogId = catalogId; // productInfo.CatalogData.a.Id;
  res.ProductId = Product.a.Id;
  res.Hardware2ProgramId = hardware2ProgramRefId;

  ////

  res.Type = Type || "";

  //

  let AP: any | undefined;
  let appXmlString = "";

  //
  // pick the main application program
  if (ManufacturerId && ApplicationPrograms && ApplicationPrograms.length > 0) {
    try {
      appXmlString = await loadOneProductDatamodelString(mainZipFile, ManufacturerId || "", ApplicationPrograms[0]);
      const appObject = await convertXmlStringToJson(appXmlString);
      if (appObject) {
        const KNX = appObject.KNX;
        const ManufacturerData = firstChildWithTag(KNX, "ManufacturerData"); // no itx
        const Manufacturer = firstChildWithTag(ManufacturerData, "Manufacturer"); // no itx
        const ApplicationPrograms = firstChildWithTag(Manufacturer, "ApplicationPrograms"); // no itx
        if (ApplicationPrograms && ApplicationPrograms?.items?.length == 1) {
          AP = ApplicationPrograms.items[0];
        }
      }
    } catch (ex) {
      logger.error("Application Program XML could not be loaded for Product ", {
        CatalogId: catalogId,
        ProductRefId: productRefId,
        Hardware2ProgramRefId: hardware2ProgramRefId,
        ex,
      });
    }
  }

  if (!AP && ApplicationPrograms && ApplicationPrograms.length > 0) {
    logger.error("No Application Program found for Product ", {
      CatalogId: catalogId,
      ProductRefId: productRefId,
      Hardware2ProgramRefId: hardware2ProgramRefId,
    });
  }
  //
  // calculate ApplicationVersion in readable form
  //
  let versionNumber = Hardware.a.VersionNumber?.trim() || "";
  if (AP?.a.ApplicationVersion) {
    const appVersion = parseInt(AP.a.ApplicationVersion);
    versionNumber = `${Math.floor(appVersion / 16)}.${appVersion % 16}`;
  }

  //
  // check if programmable
  //

  let isProgrammable = "No"; // no itx
  if (AP?.a.MaskVersion) {
    isProgrammable = KnxBridge.SupportedMasks.includes(AP?.a.MaskVersion.trim().split("-")[1]) ? "Potential" : "No";
  }

  //
  // calculate application names in all languages
  //
  let applicationNames: any = {};
  if (languageData) {
    Array.from(languageData).forEach((item: any) => (applicationNames[item[0]] = item[1].Name));
  }

  //
  // get all medium types from master infos
  //
  const allMediumTypes = masterInfos?.mediumTypesInfos;

  //
  // we need to map them to the names from the masterXml
  //
  let mediumTypes = "";
  for (const applicationMediumType of (Hardware2Program.a.MediumTypes || "").split(" ").filter(Boolean)) {
    const mediumType = allMediumTypes.find((item: { a: { Id: any } }) => {
      return item.a.Id == applicationMediumType;
    });
    const mediumTypeName = mediumType?.a?.Name || applicationMediumType;

    mediumTypes = mediumTypes == "" ? mediumTypeName : `${mediumTypes},${mediumTypeName}`;
  }

  const filteredManufacturer = masterInfos.manufacturerInfos.find((item: { a: { Id: any } }) => {
    return item.a.Id == ManufacturerId;
  });
  const manufacturerName = filteredManufacturer?.a?.Name?.trim() || ManufacturerId || "";
  const manufacturerId = filteredManufacturer?.a?.Id?.trim() || ManufacturerId || "";

  res = {
    ...res,

    ManufacturerName: cleanEncode(manufacturerName),
    ManufacturerId: manufacturerId,

    VersionNumber: versionNumber?.trim(),

    ProductNames: cleanEncode(JSON.stringify(productNames)),
    ProductDescriptions: cleanEncode(JSON.stringify(productDescriptions)),

    MediumType: mediumTypes,

    //////////////////// From Catalog.xml //////////////////////

    DefaultProductName: cleanEncode(defaultProductName),
    Type: cleanEncode(CatalogItem.a?.Type?.trim() || Type || ""),

    //////////////////// From Hardware.xml //////////////////////
    SerialNumber: cleanEncode(Hardware.a.SerialNumber?.trim()),
    BusCurrent: Hardware.a.BusCurrent?.trim() || "",

    WidthInMillimeter: Product.a.WidthInMillimeter?.trim() || "",
    IsRailMounted: Product.a.IsRailMounted?.trim() || "",
    OrderNumber: cleanEncode(Product.a.OrderNumber?.trim() || ""),

    ///
    // from the application program
    //

    IconFile: AP ? AP.a.IconFile || "" : "",

    DefaultLanguage: AP ? AP.a.DefaultLanguage?.trim() : defaultLanguage,
    Applications: AP ? cleanEncode(JSON.stringify(applicationNames)) : "",
    ApplicationVersion: AP ? AP.a.ApplicationVersion?.trim() : "",
    DefaultApplication: AP ? cleanEncode(AP.a.Name?.trim()) : "",
    Id: AP ? AP.a.Id : "", // Application Id
    HasApplicationProgram: AP ? "true" : "false", // no itx

    PeiType: AP ? AP.a.PeiType?.trim() : "",
    MaskVersion: AP ? AP.a.MaskVersion?.trim() : "",
    LoadProcedureStyle: AP ? AP.a.LoadProcedureStyle?.trim() : "",
    ProgramType: AP ? AP.a.ProgramType?.trim() : "",
    Linkable: AP ? AP.a.Linkable?.trim() : "",
    ApplicationNumber: AP ? AP.a.ApplicationNumber?.trim() : "",
    DynamicTableManagement: AP ? AP.a.DynamicTableManagement?.trim() : "",
    MinEtsVersion: AP ? AP.a.MinEtsVersion?.trim() : "",
    CreatedFromLegacySchemaVersion: AP ? AP.a.CreatedFromLegacySchemaVersion?.trim() : "",
    NonRegRelevantDataVersion: AP ? AP.a.NonRegRelevantDataVersion?.trim() : "",
    ReplacesVersions: AP ? AP.a.ReplacesVersions?.trim() : "",
    Hash: AP ? AP.a.Hash?.trim() : Product.a.Hash?.trim() || Hardware2Program.a.Hash?.trim() || "",
    OriginalManufacturer: AP ? AP.a?.OriginalManufacturer : Hardware.a.OriginalManufacturer?.trim() || "",
    Programmable: isProgrammable,
    Variants: cleanEncode(JSON.stringify([])),
    Note: "",
    NoChannels: "false", // no itx
    DeviceAddress: "Unset", // no itx
    SpecialUsage: "Unset", // no itx
    IsDali: "false", // no itx

    ///////////////////////////////////////////

    FromEts: "",

    //
    // temporary field only during upload
    //
    ApplicationPrograms: ApplicationPrograms || [],
  };

  return res;
}
